import { T, useT } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { useState } from 'react';

import { useRulesEngine } from '@/modules/rulesEngine';
import {
  AlertInfoCard,
  CtaButton,
  Screen,
  ScreenHeading,
  TextField,
  formatInteger,
} from '@/modules/ui';

// Single-input form for orgs whose `eligibility_check_fields` includes
// EMPID. Label + helper copy come from the org row (`employee_id_*`), so
// the same screen works for Boeing's BEMSID, Werner's "Employee ID", etc.
export function EligibilityFieldsForm() {
  const t = useT();
  const {
    draft,
    setDraft,
    selectedOrg,
    runEligibilityCheck,
    eligibilityCheckResult,
    setEligibilityCheckResult,
  } = useRulesEngine();

  // Local-only: empty-field validation. isChecking + passed live in
  // eligibilityCheckResult so the form doesn't track its own submit / error.
  const [showRequiredError, setShowRequiredError] = useState(false);

  const employeeId = draft.employeeId ?? '';

  const onChangeText = (text: string) => {
    setDraft((prev) => ({ ...prev, employeeId: text }));
    setShowRequiredError(false);
    // Clear any prior submit result so a previous "couldn't verify" error
    // doesn't hang around once the user starts editing again.
    setEligibilityCheckResult(undefined);
  };

  const onContinue = async () => {
    if (!employeeId.trim()) {
      setShowRequiredError(true);
      return;
    }
    await runEligibilityCheck();
  };

  if (!selectedOrg) return null;

  const isChecking = eligibilityCheckResult?.isChecking === true;

  // Field label + help copy. Backend-provided values (`employeeIdDisplayName` /
  // `employeeIdDescription`) always take top priority; the hardcoded copy is a
  // fallback only when the org row omits them (e.g. the Amwell health-plan org
  // carries neither, so the label becomes "ID Number" and the help text points
  // at the insurance card). This mirrors Sparta's
  // EmployerOrHealthPlanVerificationContainer, except the backend description is
  // preferred over the selectHealthPlan/BEMSID hardcodes rather than last.
  const label = selectedOrg.employeeIdDisplayName
    ? selectedOrg.employeeIdDisplayName
    : selectedOrg.selectHealthPlan
      ? t('ID Number')
      : t('Your Employee ID');

  const supportDescription = selectedOrg.employeeIdDescription
    ? selectedOrg.employeeIdDescription
    : selectedOrg.employeeIdDisplayName === 'BEMSID'
      ? t(
          'Your BEMSID is your unique Boeing employee ID. If you are the spouse or dependent of a Boeing employee, please enter the BEMSID of the employee.'
        )
      : selectedOrg.selectHealthPlan
        ? t('Your insurance ID Number can be found on your insurance card.')
        : undefined;

  // Only the empty-field validation surfaces inline here. A failed eligibility
  // check routes the user onward (to /employee-search) rather than showing a
  // "couldn't verify" error on this data-entry form.
  const errorMessage = showRequiredError ? t('Required') : undefined;

  return (
    <Screen
      footer={
        <CtaButton
          isBusy={isChecking}
          isDisabled={isChecking}
          onPress={onContinue}
          testID='eligibility-fields-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('Your organization needs you to give more information')}
          subtitle={t(
            "We'll use this information to verify your identity and get you set up with Vida"
          )}
        />

        <TextField
          value={employeeId}
          onChangeText={onChangeText}
          format={selectedOrg.employeeIdInteger ? formatInteger : undefined}
          placeholder={label}
          autoCapitalize='none'
          autoCorrect={false}
          inputMode={selectedOrg.employeeIdInteger ? 'numeric' : 'text'}
          keyboardType={selectedOrg.employeeIdInteger ? 'number-pad' : 'default'}
          errorMessage={errorMessage}
          testID='eligibility-empid'
        />

        {supportDescription ? (
          <AlertInfoCard title={t('Where can I find this?')} description={supportDescription} />
        ) : null}
      </VStack>
    </Screen>
  );
}
